import React, { useEffect, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, ExternalLink, RefreshCw } from 'lucide-react'
import { useFxRateStore, type FxZoneData } from '../store/fxRateStore'
import { buttonClass } from './ui/Button'

interface FxRateModalProps {
  isOpen: boolean
  onClose: () => void
}

type ZoneData = FxZoneData

const ZONES = [
  { key: 'Extreme Low',  label: 'Extreme\nLow',  color: '#22c55e', bg: 'bg-green-500',  text: 'text-gain',  range: [0, 10] },
  { key: 'Low',          label: 'Low',            color: '#86efac', bg: 'bg-green-300',  text: 'text-green-500 dark:text-green-300',  range: [10, 30] },
  { key: 'Neutral',      label: 'Neutral',        color: '#fbbf24', bg: 'bg-yellow-400', text: 'text-yellow-600 dark:text-yellow-400', range: [30, 70] },
  { key: 'High',         label: 'High',           color: '#fb923c', bg: 'bg-orange-400', text: 'text-orange-500 dark:text-orange-400', range: [70, 90] },
  { key: 'Extreme High', label: 'Extreme\nHigh',  color: '#ef4444', bg: 'bg-red-500',    text: 'text-loss',      range: [90, 100] },
]

const ZoneGauge: React.FC<{ zone: ZoneData }> = ({ zone }) => {
  const { current, currentPercentile, currentZone, min, max, mean, boundaries, history } = zone
  const currentZoneInfo = ZONES.find(z => z.key === currentZone) ?? ZONES[2]

  const pts = history.map(h => h.rate)
  const chartMin = Math.min(...pts)
  const chartMax = Math.max(...pts)
  const chartRange = chartMax - chartMin || 1
  const W = 500, H = 60
  const coords = pts.map((p, i) => {
    const x = (i / (pts.length - 1)) * W
    const y = H - ((p - chartMin) / chartRange) * H
    return `${x},${y}`
  }).join(' ')

  const toBarPct = (v: number) => ((v - min) / (max - min)) * 100
  const markerPct = Math.min(100, Math.max(0, toBarPct(current)))

  return (
    <div className="space-y-4">
      {/* Zone badge + current rate */}
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">USD/THB Zone ({Math.round(zone.dataPoints / 260)}Y range · {zone.dataPoints} days)</div>
          <div className="flex items-center gap-2">
            <span className={`text-lg font-bold px-3 py-1 rounded-full text-white ${currentZoneInfo.bg}`}>
              {currentZone}
            </span>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Percentile {currentPercentile}th
            </span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-gray-900 dark:text-white">฿{current.toFixed(4)}</div>
          <div className="text-xs text-gray-400">Mean: ฿{mean.toFixed(2)}</div>
        </div>
      </div>

      {/* Zone bar */}
      <div className="relative">
        <div className="flex h-5 rounded-full overflow-hidden">
          {ZONES.map(z => (
            <div
              key={z.key}
              className={z.bg}
              style={{ width: `${z.range[1] - z.range[0]}%` }}
            />
          ))}
        </div>

        {/* Boundary value labels */}
        <div className="relative mt-1 h-4">
          {[
            { pct: 0,   val: min },
            { pct: toBarPct(boundaries.p10), val: boundaries.p10 },
            { pct: toBarPct(boundaries.p30), val: boundaries.p30 },
            { pct: toBarPct(boundaries.p70), val: boundaries.p70 },
            { pct: toBarPct(boundaries.p90), val: boundaries.p90 },
            { pct: 100, val: max },
          ].map(({ pct, val }, i) => (
            <span
              key={i}
              className="absolute text-[9px] text-gray-400 -translate-x-1/2"
              style={{ left: `${pct}%` }}
            >
              {val.toFixed(1)}
            </span>
          ))}
        </div>

        {/* Marker */}
        <div
          className="absolute top-0 -translate-x-1/2 flex flex-col items-center pointer-events-none"
          style={{ left: `${markerPct}%` }}
        >
          <div className="w-0.5 h-5 bg-gray-900 dark:bg-white" />
          <div className="w-0 h-0 border-l-[5px] border-r-[5px] border-t-[6px] border-l-transparent border-r-transparent border-t-gray-900 dark:border-t-white" />
        </div>
      </div>

      {/* Zone labels */}
      <div className="flex text-center">
        {ZONES.map(z => (
          <div key={z.key} style={{ width: `${z.range[1] - z.range[0]}%` }}>
            <div className={`text-[9px] font-medium leading-tight whitespace-pre-line ${z.text} ${currentZone === z.key ? 'font-bold' : ''}`}>
              {z.label}
            </div>
          </div>
        ))}
      </div>

      {/* History chart */}
      {pts.length > 2 && (
        <div className="mt-2">
          <div className="text-xs text-gray-400 mb-1">History ({zone.dataPoints} trading days)</div>
          <svg width="100%" viewBox={`0 0 ${W} ${H + 4}`} preserveAspectRatio="none" className="h-14">
            {/* Zone bands on chart */}
            {[
              { from: boundaries.p90, to: max,              color: '#ef444420' },
              { from: boundaries.p70, to: boundaries.p90,   color: '#fb923c20' },
              { from: boundaries.p30, to: boundaries.p70,   color: '#fbbf2420' },
              { from: boundaries.p10, to: boundaries.p30,   color: '#86efac20' },
              { from: min,            to: boundaries.p10,   color: '#22c55e20' },
            ].map((band, i) => {
              const y1 = H - ((band.to - chartMin) / chartRange) * H
              const y2 = H - ((band.from - chartMin) / chartRange) * H
              return <rect key={i} x="0" y={y1} width={W} height={Math.max(0, y2 - y1)} fill={band.color} />
            })}
            {/* Mean line */}
            {(() => {
              const meanY = H - ((mean - chartMin) / chartRange) * H
              return <line x1="0" y1={meanY} x2={W} y2={meanY} stroke="#94a3b8" strokeWidth="0.8" strokeDasharray="4,3" />
            })()}
            {/* Rate line */}
            <polyline points={coords} fill="none" stroke={currentZoneInfo.color} strokeWidth="1.5" strokeLinejoin="round" />
            {/* Current dot */}
            {(() => {
              const lastX = W
              const lastY = H - ((pts[pts.length - 1] - chartMin) / chartRange) * H
              return <circle cx={lastX} cy={lastY} r="3" fill={currentZoneInfo.color} />
            })()}
          </svg>
          <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
            <span>{history[0]?.date}</span>
            <span className="text-gray-400">— — mean</span>
            <span>{history[history.length - 1]?.date}</span>
          </div>
        </div>
      )}
    </div>
  )
}

const FxRateModal: React.FC<FxRateModalProps> = ({ isOpen, onClose }) => {
  const modalRef = useRef<HTMLDivElement>(null)
  const [period, setPeriod] = useState<1 | 3 | 5>(1)
  const { zoneCache, zoneErrors, loadingYears, loadedAt, loadZoneData } = useFxRateStore()

  const zoneData: ZoneData | null = zoneCache[period] ?? null
  const zoneLoading = loadingYears.has(period)
  const error = zoneErrors[period] ?? null
  const lastUpdated = loadedAt[period] ?? null

  useEffect(() => {
    if (isOpen) loadZoneData(period)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  useEffect(() => {
    if (isOpen) loadZoneData(period)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period])

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    if (isOpen) document.addEventListener('keydown', h)
    return () => document.removeEventListener('keydown', h)
  }, [isOpen, onClose])

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) onClose()
    }
    if (isOpen) document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [isOpen, onClose])

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="sheet fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
        >
          <motion.div
            ref={modalRef}
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
              <div>
                <h2 className="text-base font-bold text-gray-900 dark:text-white">Exchange Rate Zone Analysis</h2>
                <p className="text-xs text-gray-400 mt-0.5">
                  {lastUpdated ? `Updated ${lastUpdated.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })}` : 'Fetching...'}
                  {' · '}Frankfurter API
                </p>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => loadZoneData(period, true)} disabled={zoneLoading}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-40">
                  <RefreshCw className={`w-4 h-4 ${zoneLoading ? 'animate-spin' : ''}`} />
                </button>
                <button onClick={() => window.open('https://raterefresher.web.app', '_blank', 'noopener,noreferrer')}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                  <ExternalLink className="w-4 h-4" />
                </button>
                <button onClick={onClose}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="overflow-y-auto flex-1 px-5 py-4">
              {error && (
                <div className="flex flex-col items-center py-8 gap-3">
                  <p className="text-sm text-loss">{error}</p>
                  <button onClick={() => loadZoneData(period, true)}
                    className={buttonClass('primary', 'sm')}>
                    ลองใหม่
                  </button>
                </div>
              )}

              {!error && (
                <>
                  {/* Period selector */}
                  <div className="flex items-center gap-1 mb-4">
                    <span className="text-xs text-gray-400 mr-2">Analysis Period:</span>
                    {([1, 3, 5] as const).map(y => (
                      <button
                        key={y}
                        onClick={() => setPeriod(y)}
                        className={`inline-flex items-center justify-center px-3 h-9 text-xs font-medium rounded-full transition-colors ${
                          period === y
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                      >
                        {y}Y
                      </button>
                    ))}
                  </div>

                  {zoneLoading || !zoneData ? (
                    <div className="flex flex-col items-center justify-center py-16 gap-3">
                      <div className="w-7 h-7 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                      <p className="text-xs text-gray-400">Analysing {period}-year data...</p>
                    </div>
                  ) : (
                    <ZoneGauge zone={zoneData} />
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            <div className="px-5 py-3 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
              <span className="text-xs text-gray-400">
                <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs">ESC</kbd> to close
              </span>
              <button onClick={() => window.open('https://raterefresher.web.app', '_blank', 'noopener,noreferrer')}
                className="text-xs text-blue-500 hover:text-blue-600 flex items-center gap-1">
                <ExternalLink className="w-3 h-3" />
                raterefresher.web.app
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default FxRateModal
